import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from 'services/api';
import { getSiteStatus } from 'services/siteStatus';
import { useClock, useConnectionStatus, SOC_SCREENS, PLACEHOLDER_ALERTS } from './useCommandData';
import useSocket, { playAlertSound } from 'hooks/useSocket';
import './commandement.css';

const PLACEHOLDER = { agents: { actifs: 0 }, sites: { actifs: 0, total: 0 }, incidents: { aujourd_hui: 0 }, rondes_jour: { complete: 0, objectif: 24 } };

function makeIcon(color) {
  return L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 6px ${color}"></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7], popupAnchor: [0, -10],
  });
}

const ICONS = { incident: makeIcon('#e53935'), souseffectif: makeIcon('#FF9100'), enattente: makeIcon('#5C7FA8'), normal: makeIcon('#00C853'), inactif: makeIcon('#888') };

function getSiteIcon(s) {
  const status = getSiteStatus(s);
  if (status.color === '#888') return ICONS.inactif;
  if (status.color === '#e53935') return ICONS.incident;
  if (status.color === '#FF9100') return ICONS.souseffectif;
  if (status.color === '#5C7FA8') return ICONS.enattente;
  return ICONS.normal;
}

function FitAll({ sites }) {
  const map = useMap();
  useEffect(() => {
    if (sites.length === 0) return;
    map.fitBounds(L.latLngBounds(sites.map(s => [parseFloat(s.latitude), parseFloat(s.longitude)])), { padding: [30, 30], maxZoom: 15 });
  }, [sites, map]);
  return null;
}

export default function CommandementCarte() {
  const navigate = useNavigate();
  const { timeStr, dateStr } = useClock();
  const [data, setData] = useState(PLACEHOLDER);
  const [sites, setSites] = useState([]);
  const [mapConfig, setMapConfig] = useState({});
  const [fitAll, setFitAll] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem('gss-alerts-muted') === 'true');

  useEffect(() => { api.get('/parametres').then(r => setMapConfig(r.data || {})).catch(() => {}); }, []);

  // WebSocket for real-time updates
  useSocket({
    'incident:nouveau': (data) => {
      playAlertSound(data.gravite || 'moyen');
      fetchData();
    },
    'agent:statut': () => fetchData(),
    'ronde:update': () => fetchData(),
  });

  const fetchData = useCallback(async () => {
    const [d, s] = await Promise.all([
      api.get('/dashboard').then(r => r.data).catch(() => null),
      api.get('/sites/map-data').then(r => r.data).catch(() => []),
    ]);
    if (d) setData(d);
    setSites(s);
  }, []);

  const { connected, reconnected, lastTimeStr } = useConnectionStatus(fetchData, 30000);

  const d = data || PLACEHOLDER;
  const center = mapConfig.carte_lat ? [parseFloat(mapConfig.carte_lat), parseFloat(mapConfig.carte_lng)] : [6.1375, 1.2123];
  const zoom = mapConfig.carte_zoom || 7;
  const paysLabel = (mapConfig.pays || 'Togo').toUpperCase();

  return (
    <div className="cmd-root">
      {!connected && (
        <div className="cmd-banner cmd-banner-error">
          ⚠️ CONNEXION PERDUE — Derniere mise a jour : {lastTimeStr} — Reconnexion en cours...
        </div>
      )}
      {reconnected && <div className="cmd-banner cmd-banner-ok">✅ CONNEXION RETABLIE</div>}

      <header className="cmd-header">
        <div className="cmd-header-left">
          <div className="cmd-logo">G</div>
          <span className="cmd-header-title">CARTE OPERATIONNELLE — {paysLabel}</span>
        </div>
        <div className="cmd-header-right">
          <span className="cmd-date">{dateStr}</span>
          <button className="cmd-sound-btn" onClick={() => setMuted(m => { const v = !m; localStorage.setItem('gss-alerts-muted', v); return v; })} title={muted ? 'Son coupe' : 'Son actif'}>
            {muted ? '\uD83D\uDD07' : '\uD83D\uDD14'}
          </button>
          <span className="cmd-clock">{timeStr}</span>
          <button className="cmd-back-btn" onClick={() => navigate('/commandement')}>RETOUR</button>
        </div>
      </header>

      <div className="cmd-kpi-overlay">
        <div className="cmd-kpi-item">
          <span className="cmd-kpi-num">{d.agents?.actifs || 0}</span>
          <span className="cmd-kpi-lbl">Agents</span>
        </div>
        <div className="cmd-kpi-sep" />
        <div className="cmd-kpi-item">
          <span className="cmd-kpi-num">{d.sites?.actifs || 0}</span>
          <span className="cmd-kpi-lbl">Sites</span>
        </div>
        <div className="cmd-kpi-sep" />
        <div className="cmd-kpi-item">
          <span className="cmd-kpi-num" style={d.incidents?.aujourd_hui > 0 ? { color: '#FF1744' } : undefined}>{d.incidents?.aujourd_hui || 0}</span>
          <span className="cmd-kpi-lbl">Incidents</span>
        </div>
        <div className="cmd-kpi-sep" />
        <div className="cmd-kpi-item">
          <span className="cmd-kpi-num">{d.rondes_jour?.complete || 0}<span className="cmd-kpi-frac">/{d.rondes_jour?.objectif || 24}</span></span>
          <span className="cmd-kpi-lbl">Rondes</span>
        </div>
      </div>

      <div className="cmd-map-zone">
        <div className="cmd-map-container">
          <MapContainer center={center} zoom={zoom} minZoom={5} maxZoom={18} style={{ height: '100%', width: '100%', background: '#0A0A0F' }} zoomControl={true}>
            <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution='&copy; OSM &copy; CARTO' />
            {fitAll && <FitAll sites={sites} />}
            {sites.map(site => (
              <Marker key={site.id} position={[parseFloat(site.latitude), parseFloat(site.longitude)]} icon={getSiteIcon(site)}>
                <Popup>
                  <div style={{ fontSize: 12, minWidth: 160, color: '#333' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{site.nom}</div>
                    {site.client_nom && <div style={{ color: '#666', marginBottom: 4 }}>{site.client_nom}</div>}
                    <div>Agents : <strong>{site.agents_presents}</strong></div>
                    <div>Statut : <span style={{ color: getSiteStatus(site).color, fontWeight: 600 }}>{getSiteStatus(site).label}</span></div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Navigation */}
          <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button onClick={() => setFitAll(f => !f)} style={{ background: 'rgba(0,0,0,0.7)', border: '1px solid #8B0000', color: '#fff', borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              TOUS LES SITES
            </button>
          </div>
        </div>

        <div className="cmd-alerts-sidebar">
          <div className="cmd-alerts-title">ALERTES ACTIVES</div>
          <div className="cmd-alerts-list">
            {PLACEHOLDER_ALERTS.map(al => (
              <div key={al.id} className="cmd-alert-card">
                <span className={`cmd-alert-dot ${al.level}`} />
                <div className="cmd-alert-body">
                  <div className="cmd-alert-type">{al.type}</div>
                  <div className="cmd-alert-desc">{al.desc}</div>
                </div>
                <span className="cmd-alert-time">{al.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="cmd-status-bar">
        {SOC_SCREENS.map(s => (
          <div key={s.label} className="cmd-status-item">
            <span className="cmd-status-led" />
            <span className="cmd-status-label">{s.label} {s.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
